import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ListVideo, Monitor } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Playlist {
  id: string;
  name: string;
  item_count: number;
}

interface AssignPlaylistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayId: string;
  displayName: string;
  businessId: string;
  currentPlaylistId: string | null;
  onAssigned: () => void;
}

export function AssignPlaylistDialog({
  open,
  onOpenChange,
  displayId,
  displayName,
  businessId,
  currentPlaylistId,
  onAssigned,
}: AssignPlaylistDialogProps) {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(currentPlaylistId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPlaylists();
      setSelectedPlaylistId(currentPlaylistId);
    }
  }, [open, currentPlaylistId]);

  const fetchPlaylists = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('signage_playlists')
        .select(`
          id,
          name,
          signage_playlist_items (id)
        `)
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      const playlistsWithCount = (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        item_count: p.signage_playlist_items?.length || 0,
      }));

      setPlaylists(playlistsWithCount);
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
      toast.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // First, remove existing default assignment
      await supabase
        .from('display_playlist_assignments')
        .delete()
        .eq('display_id', displayId)
        .eq('is_default', true);

      // Then add new assignment if a playlist is selected
      if (selectedPlaylistId) {
        const { error } = await supabase
          .from('display_playlist_assignments')
          .upsert({
            display_id: displayId,
            playlist_id: selectedPlaylistId,
            is_default: true,
          }, {
            onConflict: 'display_id,playlist_id',
          });

        if (error) throw error;
      }

      toast.success(selectedPlaylistId ? 'Playlist assigned' : 'Playlist unassigned');
      onAssigned();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to assign playlist:', err);
      toast.error('Failed to assign playlist');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Assign Playlist
          </DialogTitle>
          <DialogDescription>
            Select a default playlist for "{displayName}"
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : playlists.length === 0 ? (
            <div className="text-center py-8">
              <ListVideo className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No playlists available</p>
              <p className="text-sm text-muted-foreground">
                Create a playlist first, then assign it to this display
              </p>
            </div>
          ) : (
            <ScrollArea className="h-64">
              <RadioGroup
                value={selectedPlaylistId || ''}
                onValueChange={(value) => setSelectedPlaylistId(value || null)}
              >
                {/* No playlist option */}
                <div className="flex items-center space-x-3 p-3 rounded-lg border mb-2 hover:bg-accent cursor-pointer">
                  <RadioGroupItem value="" id="none" />
                  <Label htmlFor="none" className="flex-1 cursor-pointer">
                    <span className="font-medium">No playlist</span>
                    <p className="text-sm text-muted-foreground">
                      Display will show the default screen
                    </p>
                  </Label>
                </div>

                {/* Playlist options */}
                {playlists.map((playlist) => (
                  <div
                    key={playlist.id}
                    className="flex items-center space-x-3 p-3 rounded-lg border mb-2 hover:bg-accent cursor-pointer"
                  >
                    <RadioGroupItem value={playlist.id} id={playlist.id} />
                    <Label htmlFor={playlist.id} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <ListVideo className="w-4 h-4 text-primary" />
                        <span className="font-medium">{playlist.name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {playlist.item_count} item{playlist.item_count !== 1 ? 's' : ''}
                      </p>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
